/**
 * N-module Pearson-player phase machine.
 * Generic strings: m{i}_items | m{i}_review | m{i}_instructions (i > 0) | post_exam | awaiting_next
 * TMUA aliases (p1/p2 / awaiting_p2 / p2Ready) are accepted and mapped by nextTmuaPearsonPhase.
 */

export const TMUA_EVENT_ALIASES = {
  p2Ready: 'nextReady',
  p2Error: 'nextError',
  p2Missing: 'nextMissing',
  p2InstructionsNext: 'instructionsNext',
};

const PHASE_FROM_TMUA = {
  p1_items: 'm0_items',
  p1_review: 'm0_review',
  p2_instructions: 'm1_instructions',
  p2_items: 'm1_items',
  p2_review: 'm1_review',
  awaiting_p2: 'awaiting_next',
  post_exam: 'post_exam',
};

const PHASE_TO_TMUA = {
  m0_items: 'p1_items',
  m0_review: 'p1_review',
  m1_instructions: 'p2_instructions',
  m1_items: 'p2_items',
  m1_review: 'p2_review',
  awaiting_next: 'awaiting_p2',
  post_exam: 'post_exam',
};

export function fromTmuaAlias(phase) {
  return PHASE_FROM_TMUA[phase] || phase;
}

export function toTmuaAlias(phase) {
  return PHASE_TO_TMUA[phase] || phase;
}

export function currentIndexFromTmuaPhase(phase) {
  if (phase === 'p2_instructions' || phase === 'p2_items' || phase === 'p2_review') return 1;
  return 0;
}

export function currentIndexFromPearsonPhase(phase, fallback = 0) {
  const generic = fromTmuaAlias(phase);
  const match = String(generic || '').match(/^m(\d+)_/);
  if (match) return Number(match[1]);
  return fallback;
}

export function parsePearsonItemsIndex(phase) {
  const generic = fromTmuaAlias(phase);
  const match = String(generic || '').match(/^m(\d+)_items$/);
  return match ? Number(match[1]) : null;
}

export function parsePearsonReviewIndex(phase) {
  const generic = fromTmuaAlias(phase);
  const match = String(generic || '').match(/^m(\d+)_review$/);
  return match ? Number(match[1]) : null;
}

export function parsePearsonInstructionsIndex(phase) {
  const generic = fromTmuaAlias(phase);
  const match = String(generic || '').match(/^m(\d+)_instructions$/);
  return match ? Number(match[1]) : null;
}

export function isPearsonItemsPhase(phase) {
  return parsePearsonItemsIndex(phase) != null;
}

export function isPearsonAwaitingPhase(phase) {
  return phase === 'awaiting_next' || phase === 'awaiting_p2';
}

function resolveNextStatus(ctx) {
  return ctx.nextStatus || ctx.p2Status || 'idle';
}

function resolveHasNext(ctx) {
  return ctx.hasNext === true || ctx.hasP2 === true;
}

function resolveSiblingExpected(ctx, currentIndex, moduleCount) {
  const nextStatus = resolveNextStatus(ctx);
  return ctx.siblingExpected === true
    || resolveHasNext(ctx)
    || nextStatus === 'loading'
    || nextStatus === 'ready'
    || currentIndex < moduleCount - 1;
}

export function nextPearsonSittingPhase(phase, event, ctx = {}) {
  const moduleCount = Number(ctx.moduleCount) > 0 ? Number(ctx.moduleCount) : 2;
  const genericPhase = fromTmuaAlias(phase);
  const genericEvent = TMUA_EVENT_ALIASES[event] || event;
  const currentIndex = Number.isFinite(Number(ctx.currentIndex))
    ? Number(ctx.currentIndex)
    : currentIndexFromPearsonPhase(phase, 0);

  const itemsIndex = parsePearsonItemsIndex(genericPhase);
  const reviewIndex = parsePearsonReviewIndex(genericPhase);
  const instrIndex = parsePearsonInstructionsIndex(genericPhase);
  const nextStatus = resolveNextStatus(ctx);
  const hasNext = resolveHasNext(ctx);
  const siblingExpected = resolveSiblingExpected(ctx, currentIndex, moduleCount);
  const awaitingReason = ctx.awaitingReason || 'lock';

  if (genericEvent === 'lastNext') {
    if (itemsIndex != null) return `m${itemsIndex}_review`;
    return genericPhase;
  }

  if (genericEvent === 'endReviewConfirm') {
    if (reviewIndex == null) return genericPhase;
    const i = reviewIndex;
    if (i === moduleCount - 1) return 'post_exam';
    if (nextStatus === 'loading' && siblingExpected) return 'awaiting_next';
    if (hasNext || nextStatus === 'ready') return `m${i + 1}_instructions`;
    return 'post_exam';
  }

  if (genericEvent === 'timer0') {
    const i = itemsIndex != null ? itemsIndex : reviewIndex;
    if (i != null) {
      if (i === moduleCount - 1) return 'post_exam';
      if (nextStatus === 'loading' && siblingExpected) return 'awaiting_next';
      if (hasNext || nextStatus === 'ready') return `m${i + 1}_instructions`;
      return 'post_exam';
    }
    if (instrIndex != null) return 'post_exam';
    return genericPhase;
  }

  if (genericEvent === 'endExamYes') {
    const onLastModule = itemsIndex === moduleCount - 1 || reviewIndex === moduleCount - 1;
    if (onLastModule) return 'post_exam';
    if (nextStatus === 'loading' && siblingExpected) return 'awaiting_next';
    return 'post_exam';
  }

  if (genericEvent === 'nextReady') {
    if (genericPhase !== 'awaiting_next') return genericPhase;
    return awaitingReason === 'continue' ? `m${currentIndex + 1}_instructions` : 'post_exam';
  }

  if (genericEvent === 'nextError' || genericEvent === 'nextMissing') {
    if (genericPhase === 'awaiting_next') return 'post_exam';
    return genericPhase;
  }

  if (genericEvent === 'instructionsNext') {
    if (instrIndex != null) return `m${instrIndex}_items`;
    return genericPhase;
  }

  if (genericEvent === 'retake') {
    return 'm0_items';
  }

  const eventItemsIndex = event === 'p1_items'
    ? 0
    : (event === 'p2_items' ? 1 : parsePearsonItemsIndex(event));
  if (itemsIndex != null && eventItemsIndex != null && eventItemsIndex < itemsIndex) {
    return genericPhase;
  }

  return genericPhase;
}
